/* ============================================================================
   Auth context for the UVA Creative Requests portal.
   Built-in email/password -> JWT. Token lives in localStorage (via api.ts).
   Exposes: <AuthProvider>, useAuth(), <RequireRole>.
   ============================================================================ */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { apiGet, apiPost, setToken, getToken, isApiError } from './api';

export type Role = 'SystemAdmin' | 'Admin' | 'Designer' | 'Requester' | 'Accounts';

export interface CurrentUser {
  id: number;
  name: string;
  email: string;
  role: Role;
}

interface LoginResponse {
  token: string;
  user: CurrentUser;
}

interface AuthState {
  user: CurrentUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  // On boot, if we have a token, resolve the current user.
  useEffect(() => {
    let active = true;
    async function boot() {
      if (!getToken()) {
        setLoading(false);
        return;
      }
      try {
        const me = await apiGet<CurrentUser>('/api/auth/me');
        if (active) setUser(me);
      } catch (e) {
        // Token invalid/expired — clear it.
        if (isApiError(e) && (e.status === 401 || e.status === 403)) setToken(null);
      } finally {
        if (active) setLoading(false);
      }
    }
    void boot();
    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await apiPost<LoginResponse>('/api/auth/login', { email, password });
    setToken(res.token);
    setUser(res.user);
  }, []);

  const logout = useCallback(() => {
    // Fire-and-forget server logout; client state is the source of truth.
    void apiPost('/api/auth/logout').catch(() => undefined);
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, loading, login, logout }),
    [user, loading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}

/** Guard a subtree by role. Renders `fallback` (or nothing) if not permitted. */
export function RequireRole({
  roles,
  children,
  fallback = null,
}: {
  roles: Role[];
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { user } = useAuth();
  if (!user || !roles.includes(user.role)) return <>{fallback}</>;
  return <>{children}</>;
}

/** Convenience: does the current user hold any of these roles? */
export function hasRole(user: CurrentUser | null, roles?: Role[] | string[]): boolean {
  if (!roles || roles.length === 0) return true;
  if (!user) return false;
  return (roles as string[]).includes(user.role);
}
